import React, { createContext, useContext, useId } from "react";
import { Menu, MenuContent, MenuItem, MenuTrigger } from "@nexus/ui";
import {
  WidgetCollapseContext,
  WidgetDataIssueContext,
  WidgetEmbedContext,
  WidgetFullscreenContext,
  WidgetMenuContext,
  WidgetTitleHiddenContext,
} from "../contexts/WidgetFeaturesContext";
import type { WidgetMenuAction } from "../contexts/WidgetFeaturesContext";
import { WidgetDataNoticeBadge, WidgetIssueState } from "./WidgetIssueState";

// Inline SVG icons — avoids adding lucide-react as a widget-sdk dependency.
function IconChevronDown({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function IconEllipsisVertical({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <circle cx="12" cy="5" r="1" />
      <circle cx="12" cy="12" r="1" />
      <circle cx="12" cy="19" r="1" />
    </svg>
  );
}

function IconMaximize2({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <path d="M15 3h6v6" />
      <path d="M9 21H3v-6" />
      <path d="M21 3l-7 7" />
      <path d="M3 21l7-7" />
    </svg>
  );
}

function IconMinimize2({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <path d="M8 3v6H2" />
      <path d="M22 15h-6v6" />
      <path d="M3 21l7-7" />
      <path d="M21 3l-7 7" />
    </svg>
  );
}

/**
 * Present while a widget is a preview in the dashboard editor. `title` is an
 * explicit author title; without one the widget's own title is used, then `fallbackTitle`.
 */
export const WidgetEditorContext = createContext<{
  title?: React.ReactNode;
  fallbackTitle?: React.ReactNode;
} | null>(null);

export interface WidgetContainerProps {
  /** Widget title shown in the header bar. Ignored (but still accepted) when
   *  `hideHeader` is true — useful for a11y labelling or skeleton states. Text,
   *  or text with elements a title variable resolved to (a flag). */
  title?: React.ReactNode;
  actions?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  framed?: boolean;
  /** Additional classes for the title in the header bar. */
  titleClassName?: string;
  /** When true the title bar is not rendered and the body fills the whole widget. */
  hideHeader?: boolean;
  /**
   * The widget draws its title inside its content (a banner's label): no title
   * bar, in the editor either, so the author sees the title once, as viewers do.
   */
  titleInContent?: boolean;
  /** When true the body element is not rendered. The widget shrinks to its
   *  header height — useful for a true collapsed state where the grid row
   *  should also contract. */
  hideBody?: boolean;
  /** Let content establish its minimum height in an auto-sized dashboard row. */
  intrinsicHeight?: boolean;
  /**
   * A few headline figures (usually `<HeaderStats />`) shown in the header of a
   * *collapsible* widget, so it still says something when collapsed — or in
   * the header of the host section a widget is embedded in. Ignored otherwise:
   * the body already shows the same data.
   */
  headerSummary?: React.ReactNode;
  /**
   * Minimum body height while the shared issue state replaces the content (no
   * data to draw), for a content-sized widget whose usual content is taller than
   * that state — so its dashboard row keeps its usual height. Fill-based layouts
   * already get their footprint from the host.
   */
  stateMinHeight?: string;
  /**
   * The widget renders `WidgetIssueState` itself, where it fits its own anatomy
   * (a heading kept in the body, an inline band). The frame then leaves the body
   * as is; its header notice for retained results is unaffected.
   */
  handlesDataIssue?: boolean;
}

// ── 3-dot menu ────────────────────────────────────────────────────────────────

function MenuButton({ actions }: { actions: WidgetMenuAction[] }) {
  return (
    <Menu>
      <MenuTrigger asChild>
        <button
          type="button"
          data-widget-control="menu"
          aria-label="Widget menu"
          title="More options"
          className="density-square-sm inline-flex items-center justify-center rounded-control text-muted transition-colors hover:bg-surface-elevated hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
        >
          <IconEllipsisVertical className="h-4 w-4" />
        </button>
      </MenuTrigger>
      <MenuContent align="end" aria-label="Widget actions">
        {actions.map((action) => (
          <MenuItem key={action.id} onSelect={() => action.handler()}>
            {action.label}
          </MenuItem>
        ))}
      </MenuContent>
    </Menu>
  );
}

// ── Widget ────────────────────────────────────────────────────────────────────

/**
 * Widget — shared frame for every SOC widget.
 *
 * Header design (DESIGN.md Tonal-Depth Rule):
 *   - Flat bg-surface (no bg-gradient-to-b — decorative gradients are banned).
 *   - A single outer border; only the focus overlay needs a header divider.
 *   - No shadow-sm on the container; the widget container uses tonal layering.
 *   - text-base (1rem / 16px), weight 600, from the shared heading scale.
 *
 * SDK features (configured via WidgetSlot from instance config):
 *   - Collapse / expand  — enabled by `WidgetCollapseContext.collapsible`
 *   - 3-dot actions menu — populated by `WidgetMenuContext.menuActions`
 */
export function Widget({
  title: suppliedTitle = "",
  actions,
  children,
  className = "",
  framed = true,
  titleClassName = "",
  hideHeader = false,
  titleInContent = false,
  hideBody = false,
  intrinsicHeight = false,
  headerSummary,
  stateMinHeight,
  handlesDataIssue = false,
}: WidgetContainerProps) {
  const bodyId = useId();
  const provided = useContext(WidgetDataIssueContext);
  const issue = handlesDataIssue ? null : provided.issue;
  const notice = provided.issue ? null : provided.notice;
  const collapseCtx = useContext(WidgetCollapseContext);
  const fullscreenCtx = useContext(WidgetFullscreenContext);
  const menuCtx = useContext(WidgetMenuContext);
  const editor = useContext(WidgetEditorContext);
  const embed = useContext(WidgetEmbedContext);
  const titleHidden = useContext(WidgetTitleHiddenContext);
  const title = titleHidden ? "" : suppliedTitle;
  const heading =
    titleHidden || titleInContent
      ? null
      : editor
        ? (editor.title ?? (title || editor.fallbackTitle))
        : title;
  const hasHeaderContent =
    Boolean(heading) ||
    (!editor &&
      (actions != null ||
        notice !== null ||
        fullscreenCtx.fullscreenable ||
        collapseCtx.collapsible ||
        menuCtx.menuActions.length > 0 ||
        (!issue && headerSummary != null && collapseCtx.collapsible)));

  // Embedded in a host section (see WidgetEmbedContext): the section is the
  // header, so the widget renders either its headline figures for that header
  // or its content without a frame (only a caption and its own actions).
  if (!editor && embed.part === "summary") return <>{issue ? null : (headerSummary ?? null)}</>;
  if (!editor && embed.part === "body" && !fullscreenCtx.fullscreen) {
    const caption = embed.captioned && title ? title : null;
    const hasMenu = menuCtx.menuActions.length > 0;
    const embedNotice = notice ? <WidgetDataNoticeBadge notice={notice} /> : null;
    return (
      <div
        className={`@container/widget flex min-w-0 flex-col ${intrinsicHeight ? "flex-1" : "h-full"} ${className}`}
      >
        {caption || actions != null || hasMenu || embedNotice ? (
          <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 pb-1">
            {caption ? (
              <h3 className={`min-w-0 flex-1 text-sm font-semibold text-primary ${titleClassName}`}>
                {caption}
              </h3>
            ) : null}
            <div className="ml-auto flex flex-wrap items-center justify-end gap-1">
              {embedNotice}
              {actions}
              {hasMenu ? <MenuButton actions={menuCtx.menuActions} /> : null}
            </div>
          </div>
        ) : null}
        {!hideBody ? (
          <div
            className={
              intrinsicHeight
                ? "flex min-w-0 flex-1 flex-col"
                : "min-h-0 min-w-0 flex-1 overflow-hidden"
            }
          >
            <WidgetBodyContent issue={issue !== null} stateMinHeight={stateMinHeight}>
              {children}
            </WidgetBodyContent>
          </div>
        ) : null}
      </div>
    );
  }

  const effectiveCollapsed = collapseCtx.collapsible && collapseCtx.collapsed;
  const effectiveHideBody = hideBody || effectiveCollapsed;

  // When rendered inside the focus overlay the panel (WidgetSlot) already owns
  // the visual frame. Widget becomes a transparent fill — no border, bg, or padding.
  const inFocusOverlay = fullscreenCtx.fullscreenable && fullscreenCtx.fullscreen;

  return (
    <div
      className={
        inFocusOverlay
          ? `@container/widget flex min-w-0 flex-col overflow-hidden h-full ${className}`
          : `@container/widget density-widget-shell ${hideHeader && notice ? "relative " : ""}flex min-w-0 flex-col overflow-hidden ${framed ? "rounded-panel border border-border bg-surface" : "bg-transparent"} ${effectiveHideBody ? "" : intrinsicHeight ? "flex-1 min-h-20" : "h-full min-h-20"} ${className}`
      }
    >
      {hideHeader && !editor && notice ? (
        // No header line to hold it: the notice sits in the top corner, over the content.
        <div className="pointer-events-none absolute top-1 right-2 z-10 rounded-control bg-surface/90 pl-2 [&_button]:pointer-events-auto">
          <WidgetDataNoticeBadge notice={notice} />
        </div>
      ) : null}
      {(!hideHeader || editor) && hasHeaderContent && (
        <div
          className={`density-widget-header flex shrink-0 flex-wrap items-center justify-between gap-2${inFocusOverlay ? " border-b border-border" : ""}`}
        >
          {heading ? (
            <h3
              className={`min-w-0 flex-1 basis-40 text-base font-semibold break-words text-brand-navy dark:text-primary ${titleClassName}`}
            >
              {heading}
            </h3>
          ) : null}
          {!editor &&
          !issue &&
          headerSummary != null &&
          collapseCtx.collapsible &&
          !inFocusOverlay ? (
            <div className="ml-auto min-w-0 max-w-full">{headerSummary}</div>
          ) : null}
          {!editor && (
            <div className="ml-auto flex max-w-full flex-wrap items-center justify-end gap-1">
              {notice ? <WidgetDataNoticeBadge notice={notice} /> : null}
              {actions != null ? <>{actions}</> : null}

              {fullscreenCtx.fullscreenable && (
                <button
                  type="button"
                  data-widget-control="focus"
                  aria-pressed={fullscreenCtx.fullscreen}
                  aria-label={fullscreenCtx.fullscreen ? "Exit focus mode" : "Open focus mode"}
                  title={fullscreenCtx.fullscreen ? "Exit focus mode" : "Open focus mode"}
                  onClick={fullscreenCtx.toggle}
                  className="density-square-sm inline-flex items-center justify-center rounded-md text-muted transition-colors hover:bg-surface-elevated hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-focus"
                >
                  {fullscreenCtx.fullscreen ? (
                    <IconMinimize2 className="h-4 w-4" />
                  ) : (
                    <IconMaximize2 className="h-4 w-4" />
                  )}
                </button>
              )}

              {collapseCtx.collapsible && (
                <button
                  type="button"
                  data-widget-control="collapse"
                  aria-expanded={!collapseCtx.collapsed}
                  aria-controls={!hideBody ? bodyId : undefined}
                  aria-label={collapseCtx.collapsed ? "Expand widget" : "Collapse widget"}
                  title={collapseCtx.collapsed ? "Expand widget" : "Collapse widget"}
                  onClick={collapseCtx.toggle}
                  className="density-square-sm inline-flex items-center justify-center rounded-md text-muted transition-colors hover:bg-surface-elevated hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-focus"
                >
                  <IconChevronDown
                    className={`h-4 w-4 transition-transform duration-200 ${collapseCtx.collapsed ? "" : "rotate-180"}`}
                  />
                </button>
              )}

              {menuCtx.menuActions.length > 0 && <MenuButton actions={menuCtx.menuActions} />}
            </div>
          )}
        </div>
      )}
      {/*
        `hideBody` (an explicit caller choice, e.g. a skeleton state) really
        omits the body from the tree. A user collapse must NOT do that: many
        widgets mount a chart imperatively onto a DOM node (an ECharts
        instance via a ref) and only redraw it when their data/config
        changes — unmounting and remounting that node on every collapse/
        expand leaves the freshly remounted node with no chart in it, since
        nothing about the widget's own data changed to make its mount effect
        re-run. Collapsing instead hides the body with `hidden` (a plain
        `display: none`, contributing no height, so the shell still shrinks
        to header height) while keeping it mounted, so its DOM node and any
        imperative chart instance survive being shown again.
      */}
      {!hideBody && (
        <div
          id={bodyId}
          inert={editor || effectiveCollapsed ? true : undefined}
          hidden={effectiveCollapsed || undefined}
          className={
            intrinsicHeight && !inFocusOverlay
              ? "flex min-w-0 flex-1 flex-col"
              : "min-h-0 min-w-0 flex-1 overflow-hidden"
          }
        >
          <WidgetBodyContent issue={issue !== null} stateMinHeight={stateMinHeight}>
            {children}
          </WidgetBodyContent>
        </div>
      )}
    </div>
  );
}

/**
 * The widget's content, or the shared issue state when it has no data to draw.
 * The content stays mounted (hidden, `display: none`) for the same reason as a
 * collapsed body: imperative charts keep their DOM node when the data returns.
 * Without an issue the wrapper is `display: contents`, so it adds no box.
 */
function WidgetBodyContent({
  issue,
  stateMinHeight,
  children,
}: {
  issue: boolean;
  stateMinHeight?: string;
  children?: React.ReactNode;
}) {
  return (
    <>
      <div className={issue ? "hidden" : "contents"} data-widget-content="">
        {children}
      </div>
      {issue ? (
        <WidgetIssueState
          className="flex-1"
          style={stateMinHeight ? { minHeight: stateMinHeight } : undefined}
        />
      ) : null}
    </>
  );
}
